import { Trans, useTranslation } from 'react-i18next';

import { ChannelLogo } from '../components/channel-logo';
import { LogoFull } from '../components/logo';
import { StoreBadges } from '../components/store-badges';
import {
  BellIcon,
  BookIcon,
  BookmarkIcon,
  GlobeIcon,
  HomeIcon,
  ShieldIcon,
  SparkleIcon,
  TranslateIcon,
  UserIcon,
} from '../components/icons';
import { ArticleCard } from '../components/article-card';
import { FALLBACK_CHANNELS, PLATFORM_LANGUAGES, DEMO_ARTICLES } from '../config/reference-data';
import type { FeedArticle } from '../types';

interface LandingProps {
  readonly onSignUp: () => void;
  readonly onLogin: () => void;
}

export function Landing({ onSignUp, onLogin }: LandingProps) {
  return (
    <main>
      <Hero onSignUp={onSignUp} onLogin={onLogin} />
      <Stats />
      <Features />
      <Steps />
      <Languages />
      <Channels />
      <Quote />
      <CTA onSignUp={onSignUp} />
      <Footer />
    </main>
  );
}

function Hero({ onSignUp, onLogin }: LandingProps) {
  const { t } = useTranslation();
  return (
    <section className="nw-hero">
      <div className="nw-hero__glow" />
      <HeroDevice />
      <div>
        <div className="nw-kicker">
          <span className="nw-kicker__dot" />
          {t('web.landing.hero.eyebrow')}
        </div>
        <h1 className="nw-hero__title">
          <Trans
            i18nKey="web.landing.hero.title"
            components={{ br: <br />, em: <em /> }}
          />
        </h1>
        <p className="nw-hero__sub">{t('web.landing.hero.subtitle')}</p>
        <div className="nw-hero__ctas">
          <button type="button" className="nw-btn nw-btn--primary nw-btn--lg" onClick={onSignUp}>
            {t('common.actions.startReading')}
          </button>
          <button type="button" className="nw-btn nw-btn--ghost nw-btn--lg" onClick={onLogin}>
            {t('nav.signIn')}
          </button>
        </div>
        <StoreBadges variant="hero" />
        <div className="nw-hero__meta">
          <span>{t('web.landing.hero.metaNoAlgorithm')}</span>
          <span className="nw-hero__meta-dot" />
          <span>{t('web.landing.hero.metaPlatformLanguages')}</span>
          <span className="nw-hero__meta-dot" />
          <span>{t('web.landing.hero.metaFreeToStart')}</span>
        </div>
      </div>
    </section>
  );
}

function HeroDevice() {
  const { t } = useTranslation();
  // Build a fake FeedArticle out of the demo article so the live ArticleCard
  // renders inside the phone preview. This keeps the marketing screen 1:1
  // with what the authenticated reader will see.
  const demo = DEMO_ARTICLES[0]!;
  const fake: FeedArticle = {
    id: demo.id,
    title: demo.title,
    summary: demo.bullets.join(' '),
    imageUrl: demo.imageUrl,
    sourceUrl: '#',
    channelId: demo.channelName,
    channelName: demo.channelName,
    channelLogo: null,
    country: null,
    language: 'en',
    publishedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    isTranslated: demo.isTranslated,
    translatedFrom: demo.translatedFrom,
    isAiProcessed: true,
    summaryBullets: demo.bullets,
    tags: demo.tags,
  };
  // Bottom tab bar matches the authenticated mobile experience —
  // Home / Explore / Guides / Profile, with Home highlighted in
  // accent. Visual only on the marketing landing.
  const tabs = [
    { key: 'home', label: t('nav.home'), Icon: HomeIcon, active: true },
    { key: 'explore', label: t('nav.explore'), Icon: SparkleIcon, active: false },
    { key: 'guides', label: t('nav.guides'), Icon: BookIcon, active: false },
    { key: 'profile', label: t('nav.profile'), Icon: UserIcon, active: false },
  ] as const;

  return (
    <div className="nw-hero__device" aria-hidden>
      <div className="nw-hero__device-glow" />
      <div className="nw-hero__device-frame">
        <div className="nw-hero__device-screen">
          <div className="nw-hero__device-content">
            <div
              style={{
                padding: '14px 16px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <LogoFull size={26} />
              <BellIcon size={18} />
            </div>
            <div style={{ display: 'flex', gap: 6, padding: '8px 16px', overflow: 'hidden' }}>
              {[
                t('web.landing.topicChips.forYou'),
                t('web.landing.topicChips.world'),
                t('web.landing.topicChips.housing'),
                t('web.landing.topicChips.visa'),
              ].map((label, i) => (
                <span key={label} className={`nw-topic-chip ${i === 0 ? 'is-on' : ''}`}>
                  {label}
                </span>
              ))}
            </div>
            <div
              style={{
                padding: '10px 14px 14px',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
              }}
            >
              <ArticleCard article={fake} />
            </div>
            {/* Mobile tab bar — pinned to the bottom of the phone screen so
                the mockup reads as the actual home page of the mobile app. */}
            <nav className="nw-hero__device-tabbar" aria-hidden>
              {tabs.map(({ key, label, Icon, active }) => (
                <span
                  key={key}
                  className={`nw-hero__device-tab ${active ? 'is-active' : ''}`}
                >
                  <Icon size={18} stroke={2} />
                  <span>{label}</span>
                </span>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stats() {
  const { t } = useTranslation();
  return (
    <div className="nw-stats">
      <div className="nw-stat-cell">
        <div className="nw-stat-cell__num">21</div>
        <div className="nw-stat-cell__label">{t('web.landing.stats.platformLanguages')}</div>
      </div>
      <div className="nw-stat-cell">
        <div className="nw-stat-cell__num">120+</div>
        <div className="nw-stat-cell__label">{t('web.landing.stats.trustedChannels')}</div>
      </div>
      <div className="nw-stat-cell">
        <div className="nw-stat-cell__num">14k</div>
        <div className="nw-stat-cell__label">{t('web.landing.stats.expatsDaily')}</div>
      </div>
      <div className="nw-stat-cell">
        <div className="nw-stat-cell__num">0</div>
        <div className="nw-stat-cell__label">{t('web.landing.stats.algorithmicFeeds')}</div>
      </div>
    </div>
  );
}

const FEATURE_KEYS = [
  { Icon: GlobeIcon, key: 'intro' },
  { Icon: TranslateIcon, key: 'translate' },
  { Icon: BellIcon, key: 'alerts' },
  { Icon: ShieldIcon, key: 'trusted' },
  { Icon: BookIcon, key: 'guides' },
  { Icon: BookmarkIcon, key: 'save' },
] as const;

function Features() {
  const { t } = useTranslation();
  return (
    <section className="nw-section">
      <div className="nw-section__head">
        <div className="nw-section__label">{t('web.landing.features.eyebrow')}</div>
        <h2 className="nw-section__title">{t('web.landing.features.title')}</h2>
        <p className="nw-section__sub">{t('web.landing.features.subtitle')}</p>
      </div>
      <div className="nw-features">
        {FEATURE_KEYS.map(({ Icon, key }) => (
          <article key={key} className="nw-feature">
            <div className="nw-feature__icon">
              <Icon size={24} stroke={2} />
            </div>
            <h3 className="nw-feature__title">
              {t(`web.landing.features.items.${key}.title` as const)}
            </h3>
            <p className="nw-feature__sub">
              {t(`web.landing.features.items.${key}.body` as const)}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

const STEP_KEYS = [
  { n: '01', key: 'language' },
  { n: '02', key: 'channels' },
  { n: '03', key: 'read' },
] as const;

function Steps() {
  const { t } = useTranslation();
  return (
    <section className="nw-section nw-section--narrow">
      <div className="nw-section__head">
        <div className="nw-section__label">{t('web.landing.steps.eyebrow')}</div>
        <h2 className="nw-section__title">{t('web.landing.steps.title')}</h2>
      </div>
      <div className="nw-steps">
        {STEP_KEYS.map(({ n, key }) => (
          <div key={n} className="nw-step">
            <div className="nw-step__num">{n}</div>
            <h3 className="nw-step__title">
              {t(`web.landing.steps.items.${key}.title` as const)}
            </h3>
            <p className="nw-step__sub">
              {t(`web.landing.steps.items.${key}.body` as const)}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Languages() {
  const { t } = useTranslation();
  return (
    <section className="nw-section">
      <div className="nw-section__head">
        <div className="nw-section__label">{t('web.landing.languages.eyebrow')}</div>
        <h2 className="nw-section__title">{t('web.landing.languages.title')}</h2>
        <p className="nw-section__sub">{t('web.landing.languages.subtitle')}</p>
      </div>
      <div className="nw-lang-grid">
        {PLATFORM_LANGUAGES.map((l) => (
          <div key={l.code} className="nw-lang-card">
            <div className="nw-lang-card__flag">{l.flag}</div>
            <div className="nw-lang-card__name">{l.name}</div>
            <div className="nw-lang-card__native">{l.native}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Channels() {
  const { t } = useTranslation();
  return (
    <section className="nw-section nw-section--narrow">
      <div className="nw-section__head">
        <div className="nw-section__label">{t('web.landing.channels.eyebrow')}</div>
        <h2 className="nw-section__title">{t('web.landing.channels.title')}</h2>
        <p className="nw-section__sub">{t('web.landing.channels.subtitle')}</p>
      </div>
      <div className="nw-channels">
        {FALLBACK_CHANNELS.map((c) => (
          <div key={c.id} className="nw-channel-card">
            <ChannelLogo name={c.name} size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="nw-channel-card__name">{c.name}</div>
              <div className="nw-channel-card__meta">
                {c.lang} · {c.country}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Quote() {
  const { t } = useTranslation();
  return (
    <section className="nw-section nw-section--narrow">
      <div className="nw-quote">
        <p className="nw-quote__body">{t('web.landing.quote.body')}</p>
        <div className="nw-quote__by">
          <div className="nw-quote__avatar">M</div>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{t('web.landing.quote.author')}</div>
            <div>{t('web.landing.quote.authorMeta')}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA({ onSignUp }: { readonly onSignUp: () => void }) {
  const { t } = useTranslation();
  return (
    <section className="nw-section nw-section--narrow">
      <div className="nw-cta">
        <h2 className="nw-cta__title">{t('web.landing.finalCta.title')}</h2>
        <p className="nw-cta__sub">{t('web.landing.finalCta.subtitle')}</p>
        <button type="button" className="nw-btn nw-btn--primary nw-btn--xl" onClick={onSignUp}>
          {t('common.actions.startReading')}
        </button>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  return (
    <footer className="nw-footer">
      <div className="nw-footer__inner">
        <div className="nw-footer__col">
          <LogoFull size={28} tagline />
          <p className="nw-footer__tagline">{t('web.landing.footer.tagline')}</p>
        </div>
        <div className="nw-footer__col">
          <h4>{t('web.landing.footer.sections.product')}</h4>
          <ul>
            <li>
              <a href="#">{t('web.landing.footer.links.webApp')}</a>
            </li>
            <li>
              <a href="#">{t('web.landing.footer.links.languages')}</a>
            </li>
          </ul>
          <StoreBadges variant="footer" />
        </div>
        <div className="nw-footer__col">
          <h4>{t('web.landing.footer.sections.company')}</h4>
          <ul>
            <li>
              <a href="#">{t('web.landing.footer.links.about')}</a>
            </li>
            <li>
              <a href="#">{t('web.landing.footer.links.editorial')}</a>
            </li>
            <li>
              <a href="#">{t('web.landing.footer.links.sources')}</a>
            </li>
            <li>
              <a href="#">{t('web.landing.footer.links.press')}</a>
            </li>
          </ul>
        </div>
        <div className="nw-footer__col">
          <h4>{t('web.landing.footer.sections.support')}</h4>
          <ul>
            <li>
              <a href="https://legal.thenewswave.app/privacy">{t('web.landing.footer.links.privacy')}</a>
            </li>
            <li>
              <a href="https://legal.thenewswave.app/terms">{t('web.landing.footer.links.terms')}</a>
            </li>
            <li>
              <a href="https://legal.thenewswave.app/cookies">{t('web.landing.footer.links.cookies')}</a>
            </li>
            <li>
              <a href="mailto:hello@thenewswave.app">{t('web.landing.footer.links.contact')}</a>
            </li>
          </ul>
        </div>
      </div>
      <div className="nw-footer__bottom">
        <span>{t('web.landing.footer.copyright', { year: currentYear })}</span>
        <span>{t('web.landing.footer.madeIn')}</span>
      </div>
    </footer>
  );
}
